/* Built-in Libraries */

import { CheckCircleIcon, ChevronDownIcon, InformationCircleIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router';
import { updateApproval } from '@/api/approvals';
import { deleteDocument } from '@/api/Sherpa/Draw';
import type { IGetProjectServiceResData } from '@/api/Sherpa/Project/modules/ProjectCore/types';
import { getProjectTeamMembers } from '@/api/Sherpa/Project/modules/ProjectTeam';
/* Custom Components*/
import AccentTitle from '@/components/UI/AccentTitle';
import { Accordion, AccordionItemT2 } from '@/components/UI/Accordion';
import Badge from '@/components/UI/Badge';
import Button from '@/components/UI/Button';
import DropDown, { type DropDownMenuItem } from '@/components/UI/DropDown';
import Input from '@/components/UI/Form/Input';
import Select from '@/components/UI/Form/Select';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import Confirm from '@/components/UI/Modal/Confirm';
import Table from '@/components/UI/Table';
import { approvalAction } from '@/constants/Approval';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import { useDocuments } from '@/hooks/ReactQueryHooks/useProjectDrawQuery';
import { useProject } from '@/hooks/ReactQueryHooks/useProjectQuery';
import { authSelector } from '@/store';
import { notifyUser } from '@/store/Toast';
import type { UpdateApprovalPayload } from '@/types/Approvals';
/* Hooks And Miscellaneous*/
import { convertEnumToText, sanitizeResponseError } from '@/utils';
import { NewDocument } from '../NewDocument';

const defaultParams = {
  perPage: 10,
  page: 1,
};

const ApprovalActions = { ...approvalAction, DELETE: 'Delete' };

interface ISelectedDocument {
  id: number;
  name: string;
  createdBy: string;
  approver: string;
  status: string;
  approvalId: number;
  note?: string;
  assignedUserId?: number;
  selectedId: string;
}

export const DocumentsList = () => {
  const { projectId, drawId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const auth = useSelector(authSelector);

  const tableRef = useRef<any>(null);
  const [selectedDocument, setSelectedDocument] = useState<{
    id: number;
    name: string;
  }>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [params, setParams] = useState<any>(defaultParams);
  const [list, setList] = useState<any[]>([]);

  const [selectedDocuments, setSelectedDocuments] = useState<ISelectedDocument[]>([]);

  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [showApproveModal, setShowApproveModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const fetchDocuments = useDocuments({
    projectId: Number(projectId),
    drawId: Number(drawId),
    params,
  });
  const documents = fetchDocuments?.data?.data || [];
  const documentsCounts = fetchDocuments?.data?.meta?.count || 0;
  const totalDocumentsApproved = fetchDocuments?.data?.meta?.totalApproved || 0;
  const pageCount = Math.ceil(documentsCounts / params.perPage);

  const fetchProject = useProject({ projectId: Number(projectId) });
  const project = fetchProject?.data?.data || ({} as IGetProjectServiceResData);

  useEffect(() => {
    if (documents?.length > 0) {
      const formattedData = documents?.map((d: any) => {
        let trashIcon = false;
        if (d?.status === 'PENDING' || d?.status === 'REJECTED') {
          if (auth.data.name === d.createdBy) {
            trashIcon = true;
          }
        }
        return { ...d, trashIcon };
      });
      setList(formattedData);
    } else {
      setList([]);
    }
  }, [documents, auth.data.name]);

  const handleModel = () => {
    setShowModal(!showModal);
  };

  const removeDocumentHandler = ({ id, name }: { id: number; name: string }) => {
    setShowRemoveConfirm(true);
    setSelectedDocument({ id, name });
  };

  const cancelRemoveDocument = () => {
    setShowRemoveConfirm(false);
  };

  const removeDocument = useMutation(
    async (documentId: number) =>
      await deleteDocument({
        projectId: Number(projectId),
        drawId: Number(drawId),
        documentId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_DOCUMENTS]);
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_DRAW]);

        setShowRemoveConfirm(false);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Document deleted successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const deleteDocumentHandler = () => {
    removeDocument.mutate(Number(selectedDocument?.id));
  };

  const onSelectedRowChange = (selectedIds: Record<string, boolean>) => {
    const selectedDoc: ISelectedDocument[] = [];

    list.forEach((invoice, index) => {
      if (Object.hasOwn(selectedIds, index.toString())) {
        selectedDoc.push({ ...invoice, selectedId: index });
      }
    });

    setSelectedDocuments(selectedDoc);
  };

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'createdBy',
      header: () => <div className='flex items-start'>Created By</div>,
      accessorKey: 'createdBy',
      cell: (props) => {
        const { getValue } = props;
        return <div className='flex items-start'>{getValue() as string}</div>;
      },
    },
    {
      id: 'name',
      header: 'Document Name',
      accessorKey: 'name',
      cell: (props) => {
        const { getValue, row } = props;
        return (
          <p className=''>
            <Link className='text-blue-600 hover:underline' to={`${location.pathname}/document/${row.original.id}`}>
              {getValue() as string}
            </Link>
          </p>
        );
      },
    },
    {
      id: 'approver',
      header: 'Approver',
      accessorKey: 'approver',
      cell: (props) => {
        const { getValue } = props;
        return <p className=''>{getValue() as string}</p>;
      },
    },

    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (props) => {
        const { getValue } = props;
        return <Badge status={getValue() as string} text={convertEnumToText(getValue() as string)} />;
      },
    },
    {
      id: 'trashIcon',
      header: '',
      accessorKey: 'trashIcon',
      size: 20,
      cell: (props) => {
        const { getValue, row } = props;
        return (
          getValue() && (
            <p>
              <TrashIcon
                className='w-5 h-5 hover:text-tertiaryColor-normal text-neutralColor-normal'
                onClick={() =>
                  removeDocumentHandler({
                    id: row.original.id,
                    name: row.original.name,
                  })
                }
              />
            </p>
          )
        );
      },
    },
  ];

  const nextPage = () => {
    const nextPage = params.page + 1;
    if (nextPage <= pageCount) {
      setParams((prev: any) => ({ ...prev, page: nextPage }));
    }
  };

  const previousPage = () => {
    const previousPage = params.page - 1;
    if (previousPage > 0) {
      setParams((prev: any) => ({ ...prev, page: previousPage }));
    }
  };

  const tablePagination = {
    pageCount,
    pageSize: params.perPage,
    currentPage: params.page,
    page: params.page,
    setPageSize: (pageSize: number) =>
      setParams((params: any) => ({
        ...params,
        perPage: pageSize,
      })),
    nextPage,
    previousPage,
  };

  const isApprovedStatus = () => {
    return selectedDocuments.every((doc) => doc.status === 'APPROVED');
  };

  const checkSameStatus = () => {
    return selectedDocuments.every((doc) => doc.status === selectedDocuments[0]?.status);
  };

  const openUserModal = () => {
    if (isApprovedStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Already Approved Document.',
          toastType: 'failed',
        })
      );
      return;
    }
    if (!checkSameStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Selected Documents should have same status.',
          toastType: 'failed',
        })
      );
      return;
    }
    setShowUserModal(true);
  };

  const openDeleteModal = () => {
    if (isApprovedStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Already Approved Document.',
          toastType: 'failed',
        })
      );
      return;
    }
    if (!checkSameStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Selected Documents should have same status.',
          toastType: 'failed',
        })
      );
      return;
    }
    setShowDeleteModal(true);
  };

  const openRejectionModal = () => {
    if (isApprovedStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Already Approved Document.',
          toastType: 'failed',
        })
      );
      return;
    }
    if (!checkSameStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Selected Documents should have same status.',
          toastType: 'failed',
        })
      );
      return;
    }
    setShowRejectModal(true);
  };

  const openApprovalModal = () => {
    if (isApprovedStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Already Approved Document.',
          toastType: 'failed',
        })
      );
      return;
    }
    if (!checkSameStatus()) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Selected Documents should have same status.',
          toastType: 'failed',
        })
      );
      return;
    }
    setShowApproveModal(true);
  };

  const cancelUserModel = () => {
    setShowUserModal(false);
  };
  const cancelRejectionModel = () => {
    setShowRejectModal(false);
  };
  const cancelApprovalModel = () => {
    setShowApproveModal(false);
  };
  const cancelDeleteModel = () => {
    setShowDeleteModal(false);
  };

  const menuItems: DropDownMenuItem[] = Object.keys(ApprovalActions).map((key) => {
    return {
      clicked: () => {
        if (key === 'DELEGATED') {
          openUserModal();
        } else if (key === 'REJECTED') {
          openRejectionModal();
        } else if (key === 'APPROVED') {
          openApprovalModal();
        } else if (key === 'DELETE') {
          openDeleteModal();
        }
      },
      name: ApprovalActions[key as keyof typeof ApprovalActions],
    };
  });

  const updateApprovalMutation = useMutation(
    async (payload: UpdateApprovalPayload) => {
      await updateApproval(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_DOCUMENTS]);
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_DRAW]);
        tableRef.current.toggleAllRowsSelected();
        setSelectedDocuments([]);
        setShowUserModal(false);
        setShowApproveModal(false);
        setShowRejectModal(false);
      },
      onError: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Failed to update Approval',
            toastType: 'failed',
          })
        );
      },
    }
  );

  const { mutate: mutateApproval } = updateApprovalMutation;

  const teamMembersQuery = useQuery(
    [SherpaProjectReactQueryKey.GET_PROJECT_MEMBERS, projectId],
    async () => {
      const response = await getProjectTeamMembers({
        projectId: Number(projectId),
      });
      return response.data?.internal || [];
    },
    {
      enabled: !!projectId,
    }
  );

  const { data: teamMembers = [] } = teamMembersQuery;

  const updatedTeamMembers = teamMembers.filter((mem) => mem?.id !== auth?.data?.id);

  const handleSubmit = () => {
    selectedDocuments.forEach((doc) => {
      mutateApproval({
        id: doc.approvalId,
        status: 'DELEGATED',
        assignedUserId: doc?.assignedUserId,
        note: doc?.note,
      });
    });
  };

  const handleRejection = () => {
    selectedDocuments.forEach((doc) => {
      mutateApproval({
        id: doc.approvalId,
        status: 'REJECTED',
        note: doc?.note,
      });
    });
  };

  const handleApproval = () => {
    selectedDocuments.forEach((doc) => {
      mutateApproval({
        id: doc.approvalId,
        status: 'APPROVED',
      });
    });
  };

  const handleBulkDelete = () => {
    selectedDocuments.forEach((doc) => {
      removeDocument.mutate(Number(doc?.id));
    });
  };

  const inputChangeHandler = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>, d: ISelectedDocument) => {
    let editedDoc: ISelectedDocument;

    if (e?.target?.name === 'note') {
      editedDoc = { ...d, note: e?.target?.value };
    }
    if (e?.target?.name === 'assignedUserId') {
      editedDoc = { ...d, assignedUserId: Number(e?.target?.value) };
    }

    const updatedSelectedDocuments = selectedDocuments.map((u) => (u.id !== editedDoc.id ? u : editedDoc));
    setSelectedDocuments(updatedSelectedDocuments);
  };

  return (
    <>
      {showModal && <NewDocument handleModal={handleModel} projectName={project?.name} />}
      {showRemoveConfirm && (
        <Confirm
          title='Remove Document'
          message={`You are about to remove ${selectedDocument?.name} document. Are you sure you want to remove?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={() => {
            deleteDocumentHandler();
          }}
          decline={cancelRemoveDocument}
        />
      )}
      {fetchDocuments.isSuccess && (
        <Accordion>
          <div className='bg-white'>
            <AccordionItemT2
              id='draw-documents'
              title={
                <div className='flex flex-row justify-between items-center mr-5'>
                  <div className=''>
                    <div className='-ml-3.5 -my-3'>
                      <AccentTitle title='Draw Documents' />
                    </div>
                    <div className='flex items-center gap-5 pl-1'>
                      <div className='flex gap-1'>
                        <CheckCircleIcon className='w-5 h-5 text-primaryColor-normal' /> <p>Approved Documents: {totalDocumentsApproved}</p>
                      </div>
                      <div>Total Documents: {documentsCounts}</div>
                    </div>
                  </div>
                  <div className='flex justify-between items-center gap-4'>
                    {selectedDocuments && selectedDocuments.length > 0 && (
                      <DropDown menuItems={menuItems}>
                        <div className='flex gap-2 items-center border p-2 rounded-md w-44 justify-between font-medium'>
                          <span>Bulk Actions</span>
                          <ChevronDownIcon className='h-4 w-4' />
                        </div>
                      </DropDown>
                    )}
                    <Button icon={<PlusIcon />} label='Add' btnStyle='primary' btnType='button' onClick={handleModel} />
                  </div>
                </div>
              }
              customClassName='border-t-2'
            >
              {list && list.length > 0 && (
                <Table
                  ref={tableRef}
                  cols={tableColumns}
                  tableData={list}
                  pagination={tablePagination}
                  enableSelection={true}
                  onSelectedRowChange={onSelectedRowChange}
                />
              )}
              {list && list.length === 0 && (
                <div className='text-center h-screen/2 flex flex-col justify-center items-center'>
                  <InformationCircleIcon className='h-12 w-12 text-tertiaryColor-normal' />
                  <h3 className='mt-1'>You have no any existing project documents</h3>
                </div>
              )}
            </AccordionItemT2>
          </div>
        </Accordion>
      )}
      {fetchDocuments.isLoading && <div className='w-full h-48 my-4 rounded animate-pulse bg-neutralColor-lighter'></div>}
      {showUserModal && (
        <Modal width='min-w-1/2'>
          <HookFormProvider>
            <HookForm onSubmit={handleSubmit}>
              <p className='text-center'>Are you sure you want to delegate all Documents?</p>
              <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes</p>
              <div className='relative overflow-x-auto border rounded-b-md w-full bg-white max-h-72 mt-6'>
                <table className='divide-y divide-gray-200 w-full overflow-y-auto'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Document Name</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Reason for Delegation</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Delegate To</th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {selectedDocuments?.map((d: ISelectedDocument) => (
                      <tr key={d.approvalId}>
                        <td className='p-4'>{d.name}</td>
                        <td className='p-4'>
                          <Input
                            type='text'
                            value={d?.note}
                            name='note'
                            error=''
                            required={true}
                            changed={(e) => inputChangeHandler(e, d)}
                          />
                        </td>
                        <td className='p-4'>
                          <Select name='assignedUserId' onChange={(e) => inputChangeHandler(e, d)} required>
                            <option value=''>Select User</option>
                            {updatedTeamMembers.map((members) => (
                              <option key={members.name} value={members.id}>
                                {members.name}
                              </option>
                            ))}
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {selectedDocuments && selectedDocuments.length === 0 && (
                  <div className='w-full'>
                    <div className='flex flex-col justify-center items-center h-40'>
                      <InformationCircleIcon className='h-8 w-8 text-tertiaryColor-normal' />
                      <h5 className='mt-1'>Please select any document.</h5>
                    </div>
                  </div>
                )}
              </div>
              <div className='flex gap-2 justify-end w-full mt-5'>
                <Button
                  label='Cancel'
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={updateApprovalMutation.isLoading}
                  onClick={() => {
                    cancelUserModel();
                  }}
                />
                <Button
                  label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Delegate'}`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={updateApprovalMutation.isLoading || selectedDocuments.length === 0}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
      {showRejectModal && (
        <Modal width='min-w-1/2'>
          <HookFormProvider>
            <HookForm onSubmit={handleRejection}>
              <p className='text-center'>Are you sure you want to reject all Documents?</p>
              <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes</p>
              <div className='relative overflow-x-auto border rounded-b-md w-full bg-white max-h-72 mt-6'>
                <table className='divide-y divide-gray-200 w-full'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Document Name</th>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Reason for Rejection</th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {selectedDocuments?.map((d: ISelectedDocument) => (
                      <tr key={d.approvalId}>
                        <td className='p-4'>{d.name}</td>
                        <td className='p-4'>
                          <Input
                            type='text'
                            value={d?.note}
                            name='note'
                            error=''
                            required={true}
                            changed={(e) => inputChangeHandler(e, d)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {selectedDocuments && selectedDocuments.length === 0 && (
                  <div className='w-full'>
                    <div className='flex flex-col justify-center items-center h-40'>
                      <InformationCircleIcon className='h-8 w-8 text-tertiaryColor-normal' />
                      <h5 className='mt-1'>Please select any document.</h5>
                    </div>
                  </div>
                )}
              </div>
              <div className='flex gap-2 justify-end w-full mt-5 border-t-2 pt-4'>
                <Button
                  label='Cancel'
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={updateApprovalMutation.isLoading}
                  onClick={() => {
                    cancelRejectionModel();
                  }}
                />
                <Button
                  label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Reject'}`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={updateApprovalMutation.isLoading || selectedDocuments.length === 0}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
      {showApproveModal && (
        <Modal>
          <HookFormProvider>
            <HookForm onSubmit={handleApproval}>
              <p className='text-center'>Are you sure you want to Approve all Documents?</p>
              <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes </p>

              <div className='flex gap-2 justify-end w-full mt-5 border-t-2 pt-4'>
                <Button
                  label='Cancel'
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={updateApprovalMutation.isLoading}
                  onClick={() => {
                    cancelApprovalModel();
                  }}
                />
                <Button
                  label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Confirm'}`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={updateApprovalMutation.isLoading}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
      {showDeleteModal && (
        <Modal width='min-w-1/2'>
          <HookFormProvider>
            <HookForm onSubmit={handleBulkDelete}>
              <p className='text-center'>Are you sure you want to remove all Documents?</p>
              <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes</p>
              <div className='relative overflow-x-auto border rounded-b-md w-full bg-white max-h-72 mt-6'>
                <table className='divide-y divide-gray-200 w-full'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Document Name</th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {selectedDocuments?.map((d: ISelectedDocument) => (
                      <tr key={d.approvalId}>
                        <td className='p-4'>{d.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {selectedDocuments && selectedDocuments.length === 0 && (
                  <div className='w-full'>
                    <div className='flex flex-col justify-center items-center h-40'>
                      <InformationCircleIcon className='h-8 w-8 text-tertiaryColor-normal' />
                      <h5 className='mt-1'>Please select any document.</h5>
                    </div>
                  </div>
                )}
              </div>
              <div className='flex gap-2 justify-end w-full mt-5 border-t-2 pt-4'>
                <Button
                  label='Cancel'
                  btnStyle='btn-neutral'
                  btnType='button'
                  disabled={updateApprovalMutation.isLoading}
                  onClick={() => {
                    cancelDeleteModel();
                  }}
                />
                <Button
                  label={`${removeDocument.isLoading ? 'Please Wait...' : 'Remove'}`}
                  btnStyle='btn-primary'
                  btnType='submit'
                  disabled={removeDocument.isLoading || selectedDocuments.length === 0}
                />
              </div>
            </HookForm>
          </HookFormProvider>
        </Modal>
      )}
    </>
  );
};
